/* eslint-disable react/jsx-no-target-blank */
import React, { useContext, useEffect } from 'react';
import _ from 'lodash';
import { Tooltip } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLMultiLobsDataHolderService } from 'ml-services-common-react';
import { MLFaqService } from 'ml-portals-services-react';
import { convertToSentenceCase } from 'ml-portals-util-js';
import appConfig from 'app-config';
import metadata from './HOProductSelectionPage.metadata.json5';
import styles from './HOProductSelectionPage.module.scss';
import messages from './HOProductSelectionPage.messages';

import wizardConfig from '../../config/ho-wizard-config.json5';

function productSelectionTooltipTemplate(translator, content, urlLink) {
    return (
        <div>
            <p className={styles.productSelectionContent}>
                {content}
            </p>
            <a href={urlLink} target="_blank" className={styles.productSelectionLink}>
                <span className={styles.productSelectionLinkText}>{translator(messages.seeAdditionalFAQTopics)}</span>
            </a>
        </div>
    );
}

function ProductSelectionTooltipComponent() {
    const isQnB = appConfig.portalCode === 'QB';
    const translator = useTranslator();
    const topic = MLFaqService.getTopic('product_choice');

    const linkUrl = `/faq/#${topic.id}`;

    return (
        <Tooltip
            id="tooltip"
            placement="bottom"
            trigger="click"
            content={productSelectionTooltipTemplate(translator, topic.short, linkUrl)}
            className={styles.tooltipContainerStyle}
        >
            <span className={isQnB ? styles.productSelectionlabel : styles.productSelectiontooltipOverride}>
                {topic.inlineQuestion.slice(0, -1)}
            </span>
        </Tooltip>
    );
}

// MLHOProductSelectionCtrl.js, ml-ho-product-selection.html
function HOProductSelectionPage(props) {
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const {
        wizardData: submissionVM, updateWizardData, goNext, currentStep, changeNextSteps, currentStepIndex, isSkipping
    } = props;

    const HO_DWELLING = 'hopdwelling';
    const HO_RENTERS = 'hoprental';
    const HO_CONDO = 'hopcondo';
    const HO_LANDLORD_HOME = 'hop_landlord_home_ml';
    const HO_LANDLORD_CONDO = 'hop_landlord_condo_ml';
    const coveragePartType = _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value', undefined);
    const isQnB = appConfig.portalCode === 'QB';

    const { steps: wizardSteps } = wizardConfig;

    // Set the lob from ffq and navigate to your info page
    useEffect(() => {
        if( !isSkipping && _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value')) {
            const selectedHOLOB = _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value');
            selectCoveragePartType(selectedHOLOB.code);
        }
    }, []);

    const updateNextSteps = (cpt) => {
        const totalSteps = wizardSteps;
        const initSteps = totalSteps.slice(0, 9);
        let remainingSteps = initSteps;
        const type = cpt.code || cpt;

        if (type === 'hoprental') {
            remainingSteps = initSteps.filter((item) => {
                return item.path !== '/construction' && item.path !== '/discount';
            });
        } else if (type === 'hop_landlord_condo_ml') {
            remainingSteps = initSteps.filter((item) => {
                return item.path !== '/additional-info-to-quote' && item.path !== '/discount';
            });
        } else {
            remainingSteps = initSteps.filter((item) => {
                return item.path !== '/discount';
            });
        }
        wizardSteps.filter((item) => {
            return remainingSteps.includes(item);
        })
            .forEach((item) => {
                item.stepProps.showInNavigation = true;
            });
        const newSteps = wizardSteps.slice(currentStepIndex, wizardSteps.length);
        changeNextSteps(newSteps);
    };

    if (!_.isUndefined(coveragePartType)) {
        updateNextSteps(coveragePartType);
        goNext();
    }

    const selectCoveragePartType = (cpt) => {
        const homeowners = {
            coverables: {
                coveragePartType_ML: cpt,
                policyAddress: undefined
            }
        };
        const homeownersVM = viewModelService.create(homeowners, 'pc', 'edge.capabilities.policyjob.lob.homeowners.dto.HoLobDataDTO');
        const lobData = {
            homeowners: homeownersVM.value
        };
        _.set(submissionVM, 'lobData', lobData);

        MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, 'hoContactDetails');
        updateNextSteps(cpt);
        goNext();
    };

    const selectRent = () => {
        selectCoveragePartType(HO_RENTERS);
    };

    const selectOwnAHome = () => {
        selectCoveragePartType(HO_DWELLING);
    };

    const selectOwnACondo = () => {
        selectCoveragePartType(HO_CONDO);
    };

    const selectLandlordOwnAHome = () => {
        selectCoveragePartType(HO_LANDLORD_HOME);
    };

    const selectLandlordOwnACondo = () => {
        selectCoveragePartType(HO_LANDLORD_CONDO);
    };

    const overrides = {
        rentButtonContainer: {
            className: isQnB ? "buttonContainer" : "hoProductSelectionButtonContainer"
        },
        ownAHomeButtonContainer: {
            className: isQnB ? "buttonContainer" : "hoProductSelectionButtonContainer"            
        },
        ownACondoButtonContainer: {
            className: isQnB ? "buttonContainer" : "hoProductSelectionButtonContainer"
        },
        landlordOwnAHomeButtonContainer: {
            className: isQnB ? "buttonContainer" : "hoProductSelectionButtonContainer"
        },
        landlordOwnACondoButtonContainer: {
            className: isQnB ? "buttonContainer" : "hoProductSelectionButtonContainer"
        },
        rentButton: {
            onClick: selectRent,
            className: isQnB ? "choiceButton secondaryButton" : "",
            content: isQnB ? convertToSentenceCase(translator(messages.rent)) : translator(messages.rent)
        },
        ownAHomeButton: {
            onClick: selectOwnAHome,
            className: isQnB ? "choiceButton secondaryButton" : "",
            content: isQnB ? convertToSentenceCase(translator(messages.ownAHome)) : translator(messages.ownAHome)
        },
        ownACondoButton: {
            onClick: selectOwnACondo,
            className: isQnB ? "choiceButton secondaryButton" : "",
            content: isQnB ? convertToSentenceCase(translator(messages.ownACondo)) : translator(messages.ownACondo)
        },
        landlordOwnAHomeButton: {
            onClick: selectLandlordOwnAHome,
            className: isQnB ? "choiceButton secondaryButton" : "",
            visible: false
        },
        landlordOwnACondoButton: {
            onClick: selectLandlordOwnACondo,
            className: isQnB ? "choiceButton secondaryButton" : "",
            visible:false
        },
    };

    const resolvers = {
        resolveComponentMap: {
            productSelectionTooltipComponent: ProductSelectionTooltipComponent
        }
    };

    const skipOnResumeIf = () => {
        return true;
    };

    return (
        <WizardPage
            skipWhen={skipOnResumeIf}
            showNext={false}
            showCancel={false}
            showPrevious={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                componentMap={resolvers.resolveComponentMap}
                overrideProps={overrides}
                model={submissionVM}
                onModelChange={updateWizardData}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

HOProductSelectionPage.propTypes = wizardProps;
export default HOProductSelectionPage;
